import {
    Hours,
    TimeSheetItem,
    TimeSheetItemStatus,
    TimeSheetItemType
} from '../features/schedule/types';
import { AbsenceBehavior } from '../features/types/types';

export type TimeSheetGridTotals = {
    total: number;
    approved: number;
    approvable: number;
    billed: number;
    billable: number;
    workHours: number;
    absenceHours: number;
    plannedHours?: number;
};

const isWillWorkAbsence = (item: TimeSheetItem): boolean => (
    item.timeshiftType === TimeSheetItemType.ABSENCE
    && item.behavior === AbsenceBehavior.WillWork
);

const shouldCountApprovedHours = (item: TimeSheetItem): boolean => (
    !isWillWorkAbsence(item)
    && (item.status === TimeSheetItemStatus.APPROVED || item.status === TimeSheetItemStatus.BILLABLE)
);

const shouldCountBilledHours = (item: TimeSheetItem): boolean => (
    !isWillWorkAbsence(item)
    && item.status === TimeSheetItemStatus.BILLABLE
);

export const getTimesheetItemSummaryHours = (item: TimeSheetItem): number => {
    if (isWillWorkAbsence(item) || (item.timeBankHours ?? 0) < 0) {
        return 0;
    }

    return item.hours ?? 0;
};

export const getTimesheetSummaryHours = (items: TimeSheetItem[]): Hours => {
    const workHours = items
        .filter((item) => item.timeshiftType === TimeSheetItemType.SHIFT)
        .reduce((sum, item) => sum + getTimesheetItemSummaryHours(item), 0);
    const absenceHours = items
        .filter((item) => item.timeshiftType === TimeSheetItemType.ABSENCE && !isWillWorkAbsence(item))
        .reduce((sum, item) => sum + getTimesheetItemSummaryHours(item), 0);
    const totalHours = workHours + absenceHours;
    const approvable = items.reduce(
        (sum, item) => sum + getTimesheetItemSummaryHours(item),
        0
    );

    return {
        totalHours,
        approved: items
            .filter(shouldCountApprovedHours)
            .reduce((sum, item) => sum + getTimesheetItemSummaryHours(item), 0),
        approvable,
        billed: items
            .filter(shouldCountBilledHours)
            .reduce((sum, item) => sum + getTimesheetItemSummaryHours(item), 0),
        workHours,
        absenceHours,
        planned: 0
    };
};

export const getTimesheetGridTotals = (items: TimeSheetItem[]): TimeSheetGridTotals => {
    const summary = getTimesheetSummaryHours(items);
    const billable = items
        .filter((item) => item.timeshiftType === TimeSheetItemType.SHIFT
            && (item.isBillable || item.status === TimeSheetItemStatus.BILLABLE))
        .reduce((sum, item) => sum + getTimesheetItemSummaryHours(item), 0);

    return {
        total: summary.totalHours,
        approved: summary.approved,
        approvable: summary.approvable ?? 0,
        billed: summary.billed,
        billable,
        workHours: summary.workHours ?? 0,
        absenceHours: summary.absenceHours ?? 0
    };
};

export const getHoursToApprove = (items: TimeSheetItem[]): number => (
    items
        .filter((item) => item.status !== TimeSheetItemStatus.APPROVED
            && item.status !== TimeSheetItemStatus.BILLABLE
            && !isWillWorkAbsence(item))
        .reduce((sum, item) => sum + getTimesheetItemSummaryHours(item), 0)
);

export const getHoursToBill = (items: TimeSheetItem[]): number => (
    items
        .filter((item) => item.timeshiftType === TimeSheetItemType.SHIFT
            && item.isBillable
            && item.status !== TimeSheetItemStatus.BILLABLE)
        .reduce((sum, item) => sum + getTimesheetItemSummaryHours(item), 0)
);
